import posthog from 'posthog-js';

// Home is /app/accounts/:accountId/dashboard. Match that segment only.
const DASHBOARD_PATH = /\/dashboard(?:\/|$)/;

export const isDashboardExceptionPath = (pathname = '') =>
  DASHBOARD_PATH.test(String(pathname).split('?')[0].split('#')[0]);

/**
 * AnalyticsHelper class to initialize and track user analytics
 * @class AnalyticsHelper
 */
export class AnalyticsHelper {
  /**
   * @constructor
   * @param {Object} [options={}] - options for analytics
   * @param {string} [options.token] - analytics token
   * @param {string} [options.apiHost] - analytics API host
   */
  constructor({ token: analyticsToken, apiHost } = {}) {
    this.analyticsToken = analyticsToken;
    this.apiHost = apiHost || 'https://eu.i.posthog.com';
    this.analytics = null;
    this.user = {};
  }

  /**
   * Initialize analytics
   * @function
   * @async
   */
  async init() {
    if (!this.analyticsToken) {
      return;
    }

    posthog.init(this.analyticsToken, {
      api_host: this.apiHost,
      capture_pageview: false,
      persistence: 'localStorage+cookie',
      // Keep this explicit so project remote config cannot turn exceptions on
      // for every route. syncExceptionAutocapture enables them on /dashboard.
      capture_exceptions: false,
      // Session replay attaches scroll/mutation observers that show up as
      // main-thread work during event sheet scrolling; keep product analytics only.
      disable_session_recording: true,
    });
    this.analytics = posthog;
    this.syncExceptionAutocapture(
      typeof window !== 'undefined' ? window.location.pathname : ''
    );
  }

  /**
   * Capture unhandled exceptions only on /dashboard routes.
   * @param {string} [pathname]
   */
  syncExceptionAutocapture(pathname) {
    if (!this.analytics) {
      return;
    }

    const path =
      pathname ||
      (typeof window !== 'undefined' ? window.location.pathname : '');

    if (isDashboardExceptionPath(path)) {
      this.analytics.startExceptionAutocapture();
      return;
    }

    this.analytics.stopExceptionAutocapture();
  }

  /**
   * Identify the user
   * @function
   * @param {Object} user - User object
   */
  identify(user) {
    if (!this.analytics || !user) {
      return;
    }

    this.user = user;
    const distinctId = `user-${this.user.id.toString()}`;

    this.analytics.identify(distinctId, {
      email: this.user.email,
      name: this.user.name,
      avatar: this.user.avatar_url,
    });

    const { accounts, account_id: accountId } = this.user;
    const [currentAccount] = (accounts || []).filter(
      account => account.id === accountId
    );
    if (currentAccount) {
      const groupId = `account-${currentAccount.id.toString()}`;
      this.analytics.group('company', groupId, {
        name: currentAccount.name,
      });
    }
  }

  /**
   * Track any event
   * @function
   * @param {string} eventName - event name
   * @param {Object} [properties={}] - event properties
   */
  track(eventName, properties = {}) {
    if (!this.analytics) {
      return;
    }
    this.analytics.capture(eventName, properties);
  }

  /**
   * Track the page views
   * @function
   * @param {string} pageName - Page name
   * @param {Object} [properties={}] - Page view properties
   */
  page(pageName, properties = {}) {
    if (!this.analytics) {
      return;
    }

    this.syncExceptionAutocapture(properties.path);
    this.analytics.capture('$pageview', { pageName, ...properties });
  }
}

// This object is shared across, the init is called in app/javascript/entrypoints/dashboard.js
export default new AnalyticsHelper(window.analyticsConfig);
